import { NgIf } from '@angular/common';
import {
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
    inject,
    signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {
    ActivatedRoute,
    NavigationEnd,
    Router,
    RouterOutlet,
} from '@angular/router';
import { MediaWatcherService } from '../../../../services/media-watcher/media-watcher.service';
import {
    ZopLoadingBarComponent,
    ZopVerticalNavigationComponent,
    ZopFullscreenComponent,
    ZopNavigationService,
    NotificationsComponent,
    ShortcutsComponent,
    ZopNavigationItem,
    ZopBrandingService,
} from '@zopup/zop-ux-core';
import { filter, Subject, takeUntil } from 'rxjs';
import { LanguagesComponent } from '../../../common/languages/languages.component';
import { MenuType } from '../../../../services/navigation/menu-type.types';
import { NavigationService } from '../../../../services/navigation/navigation.service';
import { ZopUxLayoutLibConfig } from '../../../../services/config/config.types';
import { StorageKey, StorageService } from '@zopup/zop-shared';
import { BranchDto, CompanyDto, TenantDto } from '@zopup/zop-types';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
@Component({
    selector: 'zop-dense-layout',
    templateUrl: './dense.component.html',
    encapsulation: ViewEncapsulation.None,
    imports: [
        ZopLoadingBarComponent,
        ZopVerticalNavigationComponent,
        MatButtonModule,
        MatIconModule,
        MatSidenavModule,
        LanguagesComponent,
        ZopFullscreenComponent,
        ShortcutsComponent,
        NotificationsComponent,
        NgIf,
        RouterOutlet,
    ]
})
export class DenseLayoutComponent implements OnInit, OnDestroy {
    @Input()
    config: ZopUxLayoutLibConfig;

    isScreenSmall: boolean;
    navigation: MenuType;
    navigationAppearance: 'default' | 'dense' = 'dense';
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    readonly branding: ZopBrandingService = inject(ZopBrandingService);

    // Track navigation hover state
    isNavigationHovered = signal<boolean>(false);

    /**
     * Constructor
     */
    constructor(
        private _navigationService: NavigationService,
        private _zopMediaWatcherService: MediaWatcherService,
        private _zopNavigationService: ZopNavigationService,
        private _storageService: StorageService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: MenuType) => {
                this.navigation = navigation;
                this.navigation.default.forEach(
                    (menuItem: ZopNavigationItem) => {
                        if (menuItem.id === 'tenant.account') {
                            menuItem.subtitle =
                                this._storageService.getItem<TenantDto>(
                                    StorageKey.tenant
                                ).businessName;
                        } else if (menuItem.id === 'tenant.branches') {
                            menuItem.subtitle =
                                this._storageService.getItem<BranchDto>(
                                    StorageKey.branch
                                ).name;
                        }
                    }
                );

                this.navigation.default = this.navigation.default.filter(
                    (menuItem: ZopNavigationItem) =>
                        menuItem.id !== 'tenant.branches' ||
                        (menuItem.id === 'tenant.branches' &&
                            this._storageService.getItem<CompanyDto>(
                                StorageKey.company
                            ).branches.length > 1)
                );
            });

        // Subscribe to media changes
        this._zopMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');

                // Change the navigation appearance
                this.navigationAppearance = this.isScreenSmall
                    ? 'default'
                    : 'dense';
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation =
            this._zopNavigationService.getComponent<ZopVerticalNavigationComponent>(
                name
            );

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    /**
     * Toggle the navigation appearance
     */
    toggleNavigationAppearance(): void {
        this.navigationAppearance =
            this.navigationAppearance === 'default' ? 'dense' : 'default';
    }

    /**
     * Handle navigation mouse enter
     */
    onNavigationMouseEnter(): void {
        if (this.navigationAppearance === 'dense') {
            this.isNavigationHovered.set(true);
        }
    }

    /**
     * Handle navigation mouse leave
     */
    onNavigationMouseLeave(): void {
        this.isNavigationHovered.set(false);
    }

    // closePanel() {
    //     // Close the side panel by navigating out of the named outlet
    //     this._router.navigate([{ outlets: { side: null } }]);
    // }
}
